import { ReactElement } from "react";

import {
  Box,
  Circle,
  Divider,
  HStack,
  Skeleton,
  Spacer,
  Stack,
  StackDirection,
  StatGroup,
  Text,
  VStack,
  useBreakpointValue,
} from "@chakra-ui/react";

import { QuotationLineModelDocumentModelContractStatusEnum } from "api";
import { DesktopOnly, MobileOnly } from "components/BreakpointVisibility";
import Card, { CardBody, CardHeader, CardProps } from "components/Card";
import ClickToCopy from "components/ClickToCopy";
import CloseButton from "components/CloseButton";
import ContractQuantityProgressBarSection from "components/ContractQuantityProgressBarSection";
import ContractStat from "components/ContractStat";
import ContractStatus from "components/ContractStatus";
import { ErrorLoadingInline } from "components/ErrorLoading";
import ShipStartEndWidget from "components/ShipStartEndWidget";
import {
  OpenRequestOrderFormButton,
  RequestOrderForm,
} from "forms/RequestOrderForm";
import useContractDetails from "hooks/api/contracts/useContractDetails";
import useFeatureFlag from "hooks/useFeatureFlag";
import usePermission from "hooks/usePermission";
import { iconSpacing } from "icons";
import { ORDERS } from "util/featureFlags";
import { DEFAULT_EMPTY_VALUE } from "util/statHelpers";
import { desktopOnly, mobileStyleBreakpoint } from "util/styleHelpers";
import ContractDetailsTabs from "./tabs/ContractDetailsTabs";
import { requestOrderButtonPadding } from "./tabs/tabPanels/orders/OrdersTab";
import MoreOrdersRequiredBadge from "./tabs/tabPanels/orders/components/MoreOrdersRequiredBadge";
import useOpenRequestOrderFormWithQueryParam from "hooks/api/orders/useOpenRequestOrderFormWithQueryParam";

type ContractDetailsCardHeaderProps = {
  contract: Contract | undefined;
  isLoading: boolean;
  isError: boolean;
  onClose(): void;
};

const contractNumberTextStyle = mobileStyleBreakpoint<string | undefined>(
  "body",
  "h2"
);
const contractNumberTextColor = mobileStyleBreakpoint<string | undefined>(
  "grey.500",
  "black"
);
const contractStatusFlexDirection = mobileStyleBreakpoint<string>(
  "column",
  "column-reverse"
) as StackDirection;
const contractNumberFlexDirection = mobileStyleBreakpoint<string>(
  "column",
  "row"
) as StackDirection;
const contractNumberAlignItems = mobileStyleBreakpoint<string>(
  "unset",
  "center"
) as StackDirection;
const contractNumberSpacing = mobileStyleBreakpoint<number>(3, 0);
const paddingX = mobileStyleBreakpoint<number>(3, 12);

const headerMinHeight = mobileStyleBreakpoint<string>("336px", "unset");
const contractNumberSize = mobileStyleBreakpoint<string>("sm", "lg");
const displayLabel = mobileStyleBreakpoint<boolean>(true, false);
const fontWeight = mobileStyleBreakpoint<string>("black", "bold");
const contractNumberLabelFontWeight = mobileStyleBreakpoint<string>(
  "semibold",
  "black"
);
const fontSize = mobileStyleBreakpoint<string>("sm", "unset");
const spacing = mobileStyleBreakpoint<number>(0, 2);

const PoNumberHeading = ({
  poNumber,
  isError,
  isLoading,
}: {
  poNumber: string | undefined;
  isError?: boolean;
  isLoading?: boolean;
}) => {
  const clickedToCopyDisplayState =
    poNumber && poNumber === DEFAULT_EMPTY_VALUE ? "none" : undefined;
  return (
    <Skeleton isLoaded={!isLoading || isError}>
      <HStack spacing={iconSpacing}>
        <Text textStyle="h4" color="inherit" fontWeight="semibold">
          PO #
        </Text>
        {!isError ? (
          <>
            <Text color="grey.500" fontWeight="semibold" fontSize="lg">
              {poNumber}
            </Text>
            <DesktopOnly>
              <ClickToCopy
                boxSize={6}
                value={poNumber}
                display={clickedToCopyDisplayState}
                label="PO Number"
              />
            </DesktopOnly>
          </>
        ) : (
          <ErrorLoadingInline />
        )}
      </HStack>
    </Skeleton>
  );
};

export const ContractDetailsCardHeader = ({
  contract,
  isError,
  isLoading,
  onClose,
}: ContractDetailsCardHeaderProps): ReactElement => {
  const size = useBreakpointValue(contractNumberSize);
  const inlinedCustomerInfo = useBreakpointValue(desktopOnly);
  const displayStatusLabel = useBreakpointValue(displayLabel);
  const statusValueFontWeight = useBreakpointValue(fontWeight);

  const isLoaded = !isLoading || isError;

  const customerName = contract?.customerName ?? "";
  const customerId = contract?.customerCode ?? "";

  const moreOrdersRequiredBadge = contract?.moreOrdersRequired ? (
    <MoreOrdersRequiredBadge />
  ) : (
    <Spacer flex={0.5} />
  );

  return (
    <CardHeader py={6} minHeight={headerMinHeight}>
      <Stack w="100%" spacing={3}>
        <Box textAlign="end">
          <CloseButton onClick={onClose} boxSize={6} />
        </Box>
        <HStack w="100%" mb={1} alignItems="flex-start">
          <Stack
            spacing={contractNumberSpacing}
            direction={contractStatusFlexDirection}
            w="100%"
          >
            <Skeleton isLoaded={isLoaded}>
              <Text as="span" textStyle={contractNumberTextStyle}>
                <Stack
                  direction={contractNumberFlexDirection}
                  spacing={spacing}
                  alignItems={contractNumberAlignItems}
                >
                  <HStack justifyContent="space-between">
                    <Text
                      color={contractNumberTextColor}
                      fontSize={fontSize}
                      fontWeight={contractNumberLabelFontWeight}
                    >
                      Contract <MobileOnly>#</MobileOnly>
                    </Text>
                    <MobileOnly>
                      <Box>{moreOrdersRequiredBadge}</Box>
                    </MobileOnly>
                  </HStack>

                  {!isError ? (
                    <>
                      <Text color="black" fontWeight="black">
                        {contract?.contractNumber}
                      </Text>
                      <DesktopOnly>
                        <ClickToCopy
                          boxSize={6}
                          value={contract?.contractNumber}
                          label="Contract Number"
                        />
                        <Spacer />
                        {moreOrdersRequiredBadge}
                      </DesktopOnly>
                    </>
                  ) : (
                    <ErrorLoadingInline textStyle={contractNumberTextStyle} />
                  )}
                </Stack>
              </Text>
            </Skeleton>
            <ContractStatus
              size={size}
              status={contract?.status}
              isLoaded={isLoaded}
              isError={isError}
              showLabel={displayStatusLabel}
              textStyle={["body"]}
              fontWeight={statusValueFontWeight}
            />
          </Stack>
          <Spacer />
        </HStack>
        {inlinedCustomerInfo ? (
          <HStack spacing={3} color="grey.500">
            <Skeleton isLoaded={!!(customerName && customerId)}>
              <HStack spacing={iconSpacing}>
                <Text
                  textStyle="h4"
                  color="inherit"
                  fontWeight="semibold"
                >{`${customerName} ${customerId}`}</Text>
                <DesktopOnly>
                  <ClickToCopy
                    boxSize={6}
                    value={customerId}
                    label="Customer ID"
                  />
                </DesktopOnly>
              </HStack>
            </Skeleton>
            <Circle bg="grey.500" size={2} />
            <PoNumberHeading
              isLoading={isLoading}
              poNumber={contract?.poNumber}
              isError={isError}
            />
          </HStack>
        ) : (
          <>
            <StatGroup>
              <VStack spacing={4} w="100%" display="flex" alignItems="left">
                <ContractStat
                  label="PO #"
                  isLoaded={isLoaded}
                  isError={isError}
                >
                  <Text
                    display="flex"
                    whiteSpace="normal"
                    overflowWrap="break-word"
                    fontWeight="black"
                  >
                    {contract?.poNumber}
                  </Text>
                </ContractStat>
                <ContractStat isLoaded={!!customerName} maxWidth="50%">
                  <Text color="grey.700" fontWeight="bold" textStyle="body">
                    {customerName}
                  </Text>
                </ContractStat>
                <ContractStat isLoaded={!!customerId} maxWidth="50%">
                  <Text color="grey.700" fontWeight="bold" textStyle="body">
                    {customerId}
                  </Text>
                </ContractStat>
              </VStack>
            </StatGroup>
          </>
        )}
      </Stack>
    </CardHeader>
  );
};

const detailsVariant = mobileStyleBreakpoint<string | undefined>(
  "rounded",
  "drawer"
);

const ContractDetailsCard = ({
  onClose = () => {
    return;
  },
  ...rest
}: {
  onClose?(): void;
} & CardProps): ReactElement => {
  const variant = useBreakpointValue(detailsVariant);
  const { data: contract, isError, isLoading } = useContractDetails();

  const {
    isOpen: requestOrderFormOpen,
    handleOpen,
    handleClose,
  } = useOpenRequestOrderFormWithQueryParam();

  const hasAddOrderPermission = usePermission(ORDERS.ADD);
  const hasAddOrderFlag = useFeatureFlag(ORDERS.ADD);
  const requestOrderConditions =
    hasAddOrderFlag &&
    hasAddOrderPermission &&
    contract?.canRequestMoreOrders &&
    contract?.status === QuotationLineModelDocumentModelContractStatusEnum.Open;

  return (
    <Card {...rest} variant={variant}>
      <Box w="100%" h="100vh" overflowY="auto">
        <ContractDetailsCardHeader
          contract={contract}
          isError={isError}
          isLoading={isLoading}
          onClose={onClose}
        />
        <Stack spacing={10} px={paddingX} py={8}>
          <ShipStartEndWidget
            loaded={!!contract}
            startDate={contract?.shipStartDate}
            endDate={contract?.shipEndDate}
          />
          <ContractQuantityProgressBarSection
            productName={contract?.productName ?? ""}
            quantityTotal={contract?.quantityTotal ?? 0}
            quantityTotalFormatted={contract?.quantityTotalFormatted ?? ""}
            quantityShipped={contract?.quantityShipped ?? 0}
            onOrder={contract?.quantityOnOrderOpen ?? 0}
            remaining={contract?.quantityRemainingToOrder ?? 0}
            uom={contract?.unitOfMeasure ?? ""}
          />
        </Stack>
        {requestOrderConditions && requestOrderFormOpen ? (
          <Box pb={4} px={paddingX}>
            <RequestOrderForm onClose={handleClose} />
          </Box>
        ) : null}
        {requestOrderConditions && !requestOrderFormOpen ? (
          <Box pb={4} px={requestOrderButtonPadding} textAlign="right">
            <OpenRequestOrderFormButton onClick={handleOpen} />
          </Box>
        ) : null}
        <Divider />
        <CardBody p={4} px={0} {...rest}>
          <ContractDetailsTabs />
        </CardBody>
      </Box>
    </Card>
  );
};

export default ContractDetailsCard;
